import { useCallback, useEffect, useRef, useState } from "react";

import { entities } from "byzantine";

import CheckManager from "../../../../../components/TransactionDetails/CheckManager/CheckManager";

type CheckPanelSectionProps = {
  transaction: API.Transaction;
  onDescriptionUpdate?: (description: string) => void;
};

type LoadedChecks = {
  transactionId: string;
  images: API.CheckImage[];
};

/**
 * Check images for the open transaction.
 *
 * Holds the transaction id the images came from because the panel keeps its
 * sections mounted while the user moves between transactions, and the fetch hook
 * keeps its last response. Without the pairing, the previous transaction's checks
 * stay on screen until the next response lands.
 */
const CheckPanelSection = ({
  transaction,
  onDescriptionUpdate,
}: CheckPanelSectionProps) => {
  const { send: fetchImages, loading: fetchLoading } =
    entities.transactions.useGetTransactionImages();
  const [loaded, setLoaded] = useState<LoadedChecks | null>(null);

  const { id: transactionId, settled_at: settledAt } = transaction;

  // Requests can resolve out of order. Track the most recently requested
  // transaction so a late response for a transaction the user has since
  // navigated away from can't clobber the current one's state.
  const requestedIdRef = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!settledAt) return;
    requestedIdRef.current = transactionId;
    fetchImages(transactionId, {
      onData: (data) => {
        if (requestedIdRef.current !== transactionId) return;
        setLoaded({
          transactionId,
          images: data.check_images ?? [],
        });
      },
    });
  }, [fetchImages, transactionId, settledAt]);

  useEffect(load, [load]);

  // useRequestWithFeedback silently drops a send() while another is in flight.
  const wasLoadingRef = useRef(false);
  const retriedForRef = useRef<string | null>(null);

  // Reset the retry guard when navigating to a different transaction so that
  // returning to a previously-visited transaction can still trigger a retry.
  useEffect(() => {
    retriedForRef.current = null;
  }, [transactionId]);

  useEffect(() => {
    if (fetchLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;
    if (
      settledAt &&
      loaded?.transactionId !== transactionId &&
      retriedForRef.current !== transactionId
    ) {
      retriedForRef.current = transactionId;
      load();
    }
  }, [fetchLoading, settledAt, transactionId, loaded?.transactionId, load]);

  const images = loaded?.transactionId === transactionId ? loaded.images : [];

  if (!images.length) return null;

  return (
    <CheckManager
      images={images}
      transactionId={transactionId}
      titleFormat="check"
      defaultOpen
      onSaveSuccess={load}
      onDescriptionUpdate={onDescriptionUpdate}
    />
  );
};

export default CheckPanelSection;
